import React, { useContext, useEffect, useState } from "react";
import PropTypes from "prop-types";
import { ThemeContext } from "contexts/ThemeContext";
import { useNavigate, useLocation } from "react-router-dom";

import {
  Breadcrumb,
  BreadcrumbItem,
  Button,
  Card,
  CardBody,
  CardTitle,
  Container,
  Row,
  Col,
} from "reactstrap";

function CardsHeader({ name, parentName, cards = [] }) {
  const { theme } = useContext(ThemeContext);
  const [userData, setUserData] = useState(null);
  const isDark = theme === "dark";
  const navigate = useNavigate();
  const location = useLocation();
  const module = getModuleFromPath(location.pathname);

  const MODULE_HEADER_CLASS = {
    hucs: "hucs-header",
    hrms: "hrms-header",
    hadm: "hadm-header",
    hans: "hans-header",
  };

  const headerBgClass = MODULE_HEADER_CLASS[module]
    ? MODULE_HEADER_CLASS[module]
    : isDark
      ? "bg-dark"
      : "bg-default";

  function getModuleFromPath(pathname) {
    if (!pathname) return null;

    const parts = pathname.split("/").filter(Boolean);
    // "/admin/hucs" -> ["admin", "hucs"]
    // "/admin/hucs/extensions" -> ["admin", "hucs", "extensions"]

    return parts[0] === "admin" ? parts[1] : parts[0];
  }

  useEffect(() => {
    const storedUser = localStorage.getItem("user");
    if (storedUser) {
      try {
        setUserData(JSON.parse(storedUser));
      } catch (err) {
        console.error("Error parsing user data:", err);
      }
    }
  }, []);

  const getWelcomeMessage = () => {
    if (!userData) return "Welcome";
    const firstName = userData.first_name || userData.firstName || "";
    const lastName = userData.last_name || userData.lastName || "";
    const fullName = `${firstName} ${lastName}`.trim();
    return fullName || `Welcome, ${userData.email || "User"}`;
  };

  const getHeaderTextColor = () => "#ffffff"; // always white

  const getCardValueColor = () => (isDark ? "#ffffff" : "#32325d");

  // Returns the icon color depending on theme and card color
  const getIconColor = (color) => {
    if (isDark) {
      // fixed bright colors in dark mode
      return iconColor[color];
    } else {
      // white icons on bright gradients in light mode
      return "#fff";
    }
  };

  // Background classes for cards
  const cardBgGradient = (color) => {
    if (!isDark) {
      switch (color) {
        case "red":
          return "bg-gradient-red";
        case "orange":
          return "bg-gradient-orange";
        case "green":
          return "bg-gradient-green";
        case "blue":
          return "bg-gradient-blue";
        case "primary":
          return "bg-gradient-primary";
        case "white":
          return "bg-white";
        default:
          return "bg-default";
      }
    } else {
      switch (color) {
        case "red":
          return "bg-dark-red";
        case "orange":
          return "bg-dark-orange";
        case "green":
          return "bg-dark-green";
        case "blue":
          return "bg-dark-blue";
        case "primary":
          return "bg-dark-primary";
        case "white":
          return "bg-dark";
        default:
          return "bg-dark";
      }
    }
  };

  // Fixed icon colors (won't change in dark mode)
  const iconColor = {
    red: "#f5365c",
    orange: "#fb6340",
    green: "#2dce89",
    blue: "#5e72e4",
    primary: "#5e72e4",
  };

  const renderCardIcon = (icon, color) => {
    if (!icon) return null;

    // Image icon (imported PNG / SVG)
    if (
      typeof icon === "string" &&
      (icon.endsWith(".png") || icon.endsWith(".svg") || icon.endsWith(".jpg"))
    ) {
      return <img src={icon} alt="" className="cards-header-icon-img" />;
    }

    // Font icon
    return (
      <i
        className={icon}
        style={{ color: getIconColor(color), fontSize: "1.5rem" }}
      />
    );
  };

  return (
    <div className={`header pb-6 ${headerBgClass}`}>
      <Container fluid>
        <div className="header-body">
          <Row className="align-items-center py-4">
            <Col lg="8" xs="7">
              <h2
                className="d-inline-block mb-0"
                style={{ color: getHeaderTextColor() }}
              >
                {getWelcomeMessage()}
              </h2>
              <h4 className="mt-2" style={{ color: getCardValueColor() }}></h4>
              <Breadcrumb
                className="d-none d-md-inline-block ml-md-4"
                listClassName={`breadcrumb-links ${isDark ? "breadcrumb-dark" : "breadcrumb-light"}`}
              >
                <BreadcrumbItem>
                  <a
                    href="/admin/home"
                    onClick={(e) => {
                      e.preventDefault();
                      navigate("/admin/home");
                    }}
                    className={isDark ? "text-white" : ""}
                  >
                    <i className="fas fa-home" />
                  </a>
                </BreadcrumbItem>
                <BreadcrumbItem>
                  <a href="#pablo" onClick={(e) => e.preventDefault()}>
                    {parentName}
                  </a>
                </BreadcrumbItem>
                <BreadcrumbItem aria-current="page" className="active">
                  {name}
                </BreadcrumbItem>
              </Breadcrumb>
            </Col>
            {/* <Col className="text-right" lg="4" xs="5">
              {userData && userData.user_role === "GLOBAL" && (
                <>
                  <Button
                    className="btn-neutral"
                    color="default"
                    href="#pablo"
                    onClick={(e) => e.preventDefault()}
                    size="sm"
                  >
                    New
                  </Button>
                  <Button
                    className="btn-neutral"
                    color="default"
                    href="#pablo"
                    onClick={(e) => e.preventDefault()}
                    size="sm"
                  >
                    Filters
                  </Button>
                </>
              )}
            </Col> */}
          </Row>

          <Row>
            {cards === "no_access" ? (
              <Col xl="6" md="8">
                <Card className="shadow-sm">
                  <CardBody className="py-4">
                    <i className="ni ni-lock-circle-open display-1 text-muted mb-3" />
                    <h2>No Module Access</h2>
                    <p className="text-muted mb-0">
                      Please contact a superadmin for more information.
                    </p>
                  </CardBody>
                </Card>
              </Col>
            ) : (
              cards.map((card, idx) => (
                <Col key={idx} md="6" xl="3">
                  <Card
                    className={`card-stats ${
                      card.onClick ? "cursor-pointer hover-shadow" : ""
                    }`}
                    onClick={card.onClick}
                    role={card.onClick ? "button" : undefined}
                    tabIndex={card.onClick ? 0 : undefined}
                    onKeyDown={
                      card.onClick
                        ? (e) => {
                            if (e.key === "Enter" || e.key === " ") {
                              card.onClick();
                            }
                          }
                        : undefined
                    }
                    style={{
                      backgroundColor: isDark ? "#2d3748" : "#fff",
                      color: getCardValueColor(),
                    }}
                  >
                    <CardBody>
                      <Row>
                        <div className="col">
                          <CardTitle
                            tag="h5"
                            className="text-uppercase text-muted mb-0"
                          >
                            {card.title}
                          </CardTitle>
                          <span className="h2 font-weight-bold mb-0">
                            {card.value}
                          </span>
                        </div>
                        <Col className="col-auto">
                          <div
                            className={`icon icon-shape ${cardBgGradient(card.color)} rounded-circle shadow`}
                          >
                            {renderCardIcon(card.icon, card.color)}
                          </div>
                        </Col>
                      </Row>
                      <p className="mt-3 mb-0 text-sm">
                        {card.percentage && (
                          <span className={`${card.changeColor} mr-2`}>
                            <i className={`fa ${card.arrow}`} />{" "}
                            {card.percentage}
                          </span>
                        )}
                        <span className="text-nowrap">{card.text}</span>
                      </p>
                    </CardBody>
                  </Card>
                </Col>
              ))
            )}
          </Row>
        </div>
      </Container>
    </div>
  );
}

CardsHeader.propTypes = {
  name: PropTypes.string,
  parentName: PropTypes.string,
  cards: PropTypes.arrayOf(
    PropTypes.shape({
      title: PropTypes.string.isRequired,
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
        .isRequired,
      icon: PropTypes.string.isRequired,
      color: PropTypes.oneOf(["red", "orange", "green", "blue", "primary"])
        .isRequired,
      text: PropTypes.string,
      percentage: PropTypes.string,
      arrow: PropTypes.string,
      changeColor: PropTypes.string,
      onClick: PropTypes.func,
    }),
  ),
};

export default CardsHeader;
